import {
  Box,
  HStack,
  Icon,
  IconButton,
  Stack,
  StackProps,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import {
  ArrowUpIcon,
  BicycleIcon,
  DeleteIcon,
} from "../../../../shared/views/icons";
import { UpdateHlaWay } from "../../../models/HlaWay";
import {
  FacilityType,
  FacilityWidth,
  FlowDirectionType,
} from "@keita/lanepatrol-sdk";
import { LightSource } from "./LightSource";
import {
  backgroundGradient,
  cycleGreenColor,
  cycleRedColor,
  multiUsePathGradient,
  roadLaneColor,
  roadShoulderColor,
  segregationColor,
  sidewalkGradient,
} from "./constants";
import { CrossSectionAdjacentFeatures } from "./CrossSectionAdjacentFeatures";
import { CreateOrUpdateHlaLane } from "../../../models/HlaLane";
import { useStores } from "../../../../shared/stores/RootStore";
import { HlaScoreEntryView } from "../../HlaScoreView";

const CrossSectionViewBase = ({
  form,
  lane,
  canEdit,
}: {
  form: UpdateHlaWay;
  lane: CreateOrUpdateHlaLane;
  canEdit: boolean;
}) => {
  const { hlaStore, projectStore } = useStores();

  const hlaWay = hlaStore.getSelected()!;
  const selectedLane = hlaStore.getSelectedLane();
  const isSelected = lane.frontFacing === selectedLane?.frontFacing;

  const showTwoLanes =
    lane.flowDirection === FlowDirectionType.TwoWay &&
    lane.facilityType !== FacilityType.Sidewalk;
  const directionkey = lane.frontFacing ? "frontFacingLane" : "backFacingLane";
  const score = hlaWay[directionkey]?.score ?? undefined;

  const rightHandTraffic = projectStore.rightHandTraffic;
  const widthPerDirection =
    facilityWidthMultiplier(lane.facilityWidthPerDirection) * 30;

  const mainLaneProps: StackProps = {};

  if (lane.facilityType === FacilityType.OffRoadBicyclePath) {
    mainLaneProps.bgColor = cycleGreenColor;
  } else if (lane.facilityType === FacilityType.OnRoadBicycleLane) {
    mainLaneProps.bgColor = cycleRedColor;
  } else if (lane.facilityType === FacilityType.MixedTrafficRoadLane) {
    mainLaneProps.bgColor = roadLaneColor;
  } else if (lane.facilityType === FacilityType.RoadShoulder) {
    mainLaneProps.bgColor = roadShoulderColor;
  } else if (lane.facilityType === FacilityType.Sidewalk) {
    mainLaneProps.bgSize = "10px 10px";
    mainLaneProps.bgImage = sidewalkGradient;
  } else if (lane.facilityType === FacilityType.MultiUsePath) {
    mainLaneProps.bgSize = "10px 10px";
    mainLaneProps.bgImage = multiUsePathGradient;
  }

  if (lane.lightSegregation) {
    mainLaneProps.borderLeft = `4px dashed ${segregationColor}`;
    mainLaneProps.borderRight = `4px dashed ${segregationColor}`;
  }

  return (
    <VStack>
      <HlaScoreEntryView score={score?.global} />

      <HStack
        borderWidth={"3px"}
        cursor={isSelected ? "auto" : "pointer"}
        _hover={{ bgColor: "primary.100" }}
        borderColor={isSelected ? "primary.500" : "transparent"}
        position="relative"
        flexShrink={0}
        overflow={"hidden"}
        borderRadius={"8px"}
        onClick={() => {
          hlaStore.setDefaultToFrontFacing(lane.frontFacing);
        }}
      >
        <HStack
          bgColor="background-color: #e5e5f7"
          bgImage={backgroundGradient}
          backgroundPosition="0 0, 10px 10px"
          bgSize="20px 20px"
          justifyContent={"center"}
          gap={0}
          alignItems="stretch"
          alignSelf="center"
          h={"80px"}
          rotate={lane.frontFacing ? `0deg` : `180deg`}
          overflow={"hidden"}
          borderRadius={"4px"}
        >
          <CrossSectionAdjacentFeatures lane={lane} side="left" />

          <HStack
            gap={1}
            alignItems={"center"}
            transitionDuration={"0.2s"}
            bgColor={"#e7c73dff"}
            {...mainLaneProps}
            flexDir={rightHandTraffic ? "row" : "row-reverse"}
          >
            {showTwoLanes && <Box w={`${widthPerDirection}px`} />}

            {showTwoLanes && <Box borderLeft={"4px dashed white"} h={"full"} />}

            <Stack
              w={`${widthPerDirection}px`}
              h={"full"}
              alignItems={"center"}
              justifyContent={"center"}
              gap={0}
            >
              <Icon color={"white"} as={ArrowUpIcon} />
              <Icon color={"white"} as={BicycleIcon} />
            </Stack>
          </HStack>

          <CrossSectionAdjacentFeatures lane={lane} side="right" />
        </HStack>
        {form.lit && <LightSource />}
      </HStack>

      <Box h={"40px"}>
        {isSelected && (
          <IconButton
            disabled={!canEdit}
            size={"xs"}
            variant={"surface"}
            colorPalette={"primary"}
            onClick={() => {
              hlaStore.code((prev) => {
                if (lane.frontFacing) {
                  prev.frontFacingLane = null;
                } else {
                  prev.backFacingLane = null;
                }
                return { ...prev };
              });
              hlaStore.setDefaultToFrontFacing(!lane.frontFacing);
            }}
          >
            <Icon as={DeleteIcon} />
          </IconButton>
        )}
      </Box>
    </VStack>
  );
};

export const CrossSectionView = observer(CrossSectionViewBase);

const facilityWidthMultiplier = (option: FacilityWidth | null) => {
  switch (option) {
    case FacilityWidth.VeryNarrow:
      return 1;
    case null:
    case FacilityWidth.Narrow:
      return 2;
    case FacilityWidth.Wide:
      return 3;
  }
};
